/**
 * Vorlage „Abmessen“.
 *
 * Ein Messbecher wird bis zur Markierung gefüllt. Die Zielmarke ist
 * hervorgehoben, ein Auge daneben erinnert daran, auf Augenhöhe abzulesen.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { ellipsePath, path } from '../paths.ts';
import { circle, compact, faded, group, line, rect, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { pourer, stream } from '../parts/kitchen.ts';
import { arrow, badge, counter, doneMark, GROUND_Y } from '../parts/stage.ts';
import { millilitresFromText } from '../inference.ts';
import { choice, defineTemplate, numberValue, textValue } from '../template.ts';

/** Messbecher: Boden auf dem Tisch, Innenhöhe für 1000 ml. */
const JUG = { cx: 104, bottomWidth: 58, topWidth: 70, height: 96 } as const;
const JUG_TOP = GROUND_Y - JUG.height;
/** Innenhöhe für 1 Liter, vom Boden gemessen. */
const LITRE_HEIGHT = 82;
/** Ausgussecke des Gießgefäßes. */
const LIP = { x: 152, y: 40 } as const;

/** Zeitplan. */
const TIME = {
  cupIn: [0.02, 0.1],
  tilt: [0.1, 0.2],
  fill: [0.18, 0.66],
  untilt: [0.64, 0.72],
  cupOut: [0.7, 0.78],
  done: [0.72, 0.76],
  reset: [0.9, 1],
} as const;

/** Flüssigkeiten. */
const LIQUIDS = { water: 'Wasser', milk: 'Milch', oil: 'Öl' } as const;
const LIQUID_TONES: Readonly<Record<keyof typeof LIQUIDS, Tone>> = {
  water: 'tint',
  milk: 'snow',
  oil: 'shade',
};

/** Höhe (y) eines Füllstands in Millilitern. */
function levelY(ml: number): number {
  return GROUND_Y - 6 - (ml / 1000) * LITRE_HEIGHT;
}

/** Halbe Innenbreite des Bechers in Höhe y. */
function halfWidthAt(y: number): number {
  return lerp(JUG.bottomWidth, JUG.topWidth, (GROUND_Y - y) / JUG.height) / 2 - 3;
}

/** Vorlage „Abmessen“. */
export const prepMeasureTemplate = defineTemplate({
  id: 'prep.measure',
  group: 'prep',
  title: 'Abmessen',
  description:
    'Die Flüssigkeit wird in den Messbecher gegossen, bis sie die Markierung erreicht. Abgelesen wird auf Augenhöhe.',
  durationSeconds: 6,
  keywords: ['abmessen', 'messbecher', 'milliliter', 'abgemessen', 'liter'],
  stillTime: 0.74,
  parameters: [
    {
      kind: 'number',
      name: 'ml',
      label: 'Menge',
      min: 10,
      max: 1000,
      step: 10,
      unit: 'ml',
      defaultValue: 250,
    },
    choice('liquid', 'Flüssigkeit', LIQUIDS, 'water'),
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    return {
      ml: millilitresFromText(text),
      liquid: lower.includes('milch')
        ? 'milk'
        : /öl\b|oel/.test(lower)
          ? 'oil'
          : lower.includes('wasser')
            ? 'water'
            : undefined,
    };
  },
  scene(values, t) {
    const target = numberValue(values, 'ml', 250);
    const tone = LIQUID_TONES[textValue(values, 'liquid', 'water') as keyof typeof LIQUIDS];
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const fill = ease.inOutSine(segment(t, ...TIME.fill)) * (1 - reset);
    const flow =
      segment(t, TIME.fill[0], TIME.fill[0] + 0.04) * (1 - segment(t, TIME.fill[1] - 0.04, TIME.fill[1]));
    const tilt =
      -70 * (ease.inOutCubic(segment(t, ...TIME.tilt)) - ease.inOutCubic(segment(t, ...TIME.untilt)));
    const cup = ease.outCubic(segment(t, ...TIME.cupIn)) * (1 - segment(t, ...TIME.cupOut));
    const surfaceY = lerp(GROUND_Y - 6, levelY(target), fill);
    const targetY = levelY(target);

    const body = path()
      .move(JUG.cx - JUG.topWidth / 2, JUG_TOP)
      .line(JUG.cx + JUG.topWidth / 2 - 6, JUG_TOP)
      .line(JUG.cx + JUG.topWidth / 2 + 4, JUG_TOP - 8)
      .line(JUG.cx + JUG.topWidth / 2, JUG_TOP + 6)
      .line(JUG.cx + JUG.bottomWidth / 2, GROUND_Y - 3)
      .quad(JUG.cx + JUG.bottomWidth / 2, GROUND_Y, JUG.cx + JUG.bottomWidth / 2 - 3, GROUND_Y)
      .line(JUG.cx - JUG.bottomWidth / 2 + 3, GROUND_Y)
      .quad(JUG.cx - JUG.bottomWidth / 2, GROUND_Y, JUG.cx - JUG.bottomWidth / 2, GROUND_Y - 3)
      .close()
      .toString();
    // Skala: alle 100 ml ein kurzer Strich, alle 250 ml ein langer.
    const marks: SceneNode[] = [];
    for (let ml = 100; ml <= 1000; ml += 50) {
      const y = levelY(ml);
      const long = ml % 250 === 0;
      if (ml % 100 !== 0 && !long) {
        continue;
      }
      const x = JUG.cx + halfWidthAt(y);
      marks.push(line(x - (long ? 18 : 10), y, x, y, { stroke: 'deep', strokeWidth: STROKE.fine }));
    }
    const liquid =
      fill > 0.005
        ? group(
            [
              rect(JUG.cx - 50, surfaceY, 100, GROUND_Y - surfaceY, 0, styles.flat(tone)),
              line(JUG.cx - 50, surfaceY, JUG.cx + 50, surfaceY, styles.detail()),
            ],
            {
              clip: body,
            },
          )
        : null;
    const eyeX = JUG.cx - JUG.topWidth / 2 - 30;
    const eye = group([
      shape(
        path()
          .move(eyeX - 10, targetY)
          .quad(eyeX, targetY - 9, eyeX + 10, targetY)
          .quad(eyeX, targetY + 9, eyeX - 10, targetY)
          .close()
          .toString(),
        styles.solid('paper'),
      ),
      circle(eyeX, targetY, 3.4, styles.flat('ink')),
    ]);
    const done = segment(t, ...TIME.done) * (1 - segment(t, 0.88, 0.9));
    return compact([
      ...counter(),
      // Henkel links
      shape(
        path()
          .move(JUG.cx - JUG.topWidth / 2 + 2, JUG_TOP + 10)
          .curve(
            JUG.cx - JUG.topWidth / 2 - 18,
            JUG_TOP + 12,
            JUG.cx - JUG.topWidth / 2 - 16,
            JUG_TOP + 50,
            JUG.cx - JUG.bottomWidth / 2 - 1,
            JUG_TOP + 54,
          )
          .toString(),
        {
          stroke: 'ink',
          strokeWidth: STROKE.outline,
        },
      ),
      shape(body, styles.flat('paper')),
      liquid,
      ...marks,
      shape(body, styles.outline()),
      shape(ellipsePath(JUG.cx, JUG_TOP, JUG.topWidth / 2 - 2, 2), {
        fill: 'none',
        stroke: 'shade',
        strokeWidth: STROKE.fine,
      }),
      // Zielmarke und Augenhöhe
      line(eyeX + 14, targetY, JUG.cx + halfWidthAt(targetY) + 6, targetY, {
        stroke: 'ink',
        strokeWidth: STROKE.detail,
        dash: '4 3',
      }),
      eye,
      badge(JUG.cx + JUG.topWidth / 2 + 48, targetY, `${target} ml`),
      flow > 0.02
        ? stream([LIP, { x: LIP.x - 8, y: LIP.y + 26 }, { x: JUG.cx + 10, y: surfaceY }], 6 * flow, t, tone)
        : null,
      faded(cup, [pourer(LIP, 28, 36, tilt, lerp(0.8, 0.15, segment(t, ...TIME.fill)), tone)]),
      faded(ease.outBack(done), [
        doneMark(JUG.cx + JUG.topWidth / 2 + 48, targetY > 100 ? targetY - 26 : targetY + 26, 1.1),
      ]),
    ]);
  },
  hints(values) {
    const target = numberValue(values, 'ml', 250);
    return [
      arrow([
        { x: JUG.cx - 8, y: GROUND_Y - 14 },
        { x: JUG.cx - 8, y: levelY(target) + 6 },
      ]),
    ];
  },
});
